import React from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
    Box,
    Typography,
    Grid,
    IconButton,
    makeStyles,
    ListItem,
    List,
    TextField,
    Divider,
    useTheme,
    useMediaQuery,
} from '@material-ui/core';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import MomentUtils from '@material-ui/pickers/adapter/moment';
import { LocalizationProvider, DateRangePicker, MobileDatePicker } from '@material-ui/pickers';
import { useStores } from '../../hooks/use-stores';
import { getPath } from '../../utils/getPath';
import PointsValue from '../../components/UI/PointsValue';
import moneyFormat from '../../utils/moneyFormat';
import { NumberInput } from '../UI/Form';
import { getPeriodDaysCount } from '../../utils/getPeriodDaysCount';
import IncDecNumberInput from '../../components/Order/fields/IncDecNumberInput';

const CartItem = observer(({ order }) => {
    const classes = useStyles();
    const { CartStore: store } = useStores();

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

    const totalPrice = () => {
        if (order.product?.price !== 'Цена по запросу') {
            if (order.values?.rentPeriod) {
                return `от ${moneyFormat(order.product?.price * getPeriodDaysCount(order.values?.rentPeriod) * order.count)} руб.`;
            } else if (order.values?.rentHours) {
                return `от ${moneyFormat(order.product?.price * order.values.rentHours * order.count)} руб.`;
            }

            return `от ${moneyFormat(order.product?.price * order.count)} руб.`;
        }

        return order.product?.price;
    };

    const renderRangePicker = () => {
        return (
            <DateRangePicker
                mask="__.__.____"
                value={order.values?.rentPeriod}
                onChange={date => store.handleChange(order.id, 'rentPeriod', date)}
                showToolbar={false}
                renderInput={(startProps, endProps) => (
                    <Box width={isSM ? '100%' : '240px'}>
                        <div className="period-input">
                            <TextField
                                type="text"
                                value={
                                    order.values?.rentPeriod[0]
                                        ? `${moment(order.values?.rentPeriod[0]).format('DD.MM.YYYY')} - ${
                                              endProps.inputProps.value || 'Дата до'
                                          }`
                                        : ''
                                }
                                onFocus={() => startProps.inputProps.onFocus()}
                                label="Дата аренды"
                                required
                                fullWidth
                            />
                            <div className="period-input__calendar-btn">
                                <img src={getPath('public/img/calendar-month.svg')} alt="" />
                            </div>
                        </div>
                    </Box>
                )}
            />
        );
    };

    const renderDatePicker = () => {
        return (
            <MobileDatePicker
                okText="Выбрать"
                cancelText=""
                showToolbar={false}
                value={order.values?.rentDate}
                onChange={date => store.handleChange(order.id, 'rentDate', date)}
                renderInput={props => (
                    <Box position="relative" width={isSM ? '100%' : '240px'}>
                        <TextField {...props} variant="standard" fullWidth helperText="" label="Дата аренды" required />
                        <div className="period-input__calendar-btn">
                            <img src={getPath('public/img/calendar-month.svg')} alt="" />
                        </div>
                    </Box>
                )}
                disableOpenPicker={false}
            />
        );
    };

    return (
        <Grid container spacing={3} key={order.id}>
            <Grid item xs={12} sm={3}>
                <Link to={order.product?.link} className={classes.imgWrap}>
                    <img src={order.product?.img} alt="" className={classes.img} />
                </Link>
            </Grid>
            <Grid item xs={12} sm={8}>
                <Box mb={1}>
                    <Link to={order.product?.link} className={classes.link}>
                        <Typography variant="h6">{order.product.name}</Typography>
                    </Link>
                </Box>

                <Box display="flex" alignItems="center" mb={1} justifyContent={{ xxs: 'space-between', sm: 'flex-start' }}>
                    <Typography variant="h6" className={classes.price}>
                        {totalPrice()}
                    </Typography>
                    {order.product.points && (
                        <Box ml={3}>
                            <PointsValue>
                                <Typography>{order.product.points}</Typography>
                            </PointsValue>
                        </Box>
                    )}
                </Box>

                <Box mt={2}>
                    <Divider />
                </Box>

                <Box mb={0.5}>
                    <List dense>
                        {order.product.properties.map((prop, index) => {
                            if (prop?.name) {
                                return (
                                    <ListItem alignItems="center" key={index} className={classes.propItem}>
                                        <Box mr={1}>
                                            <Typography variant="body2" color="textSecondary">
                                                {prop?.name}:
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="textSecondary">
                                            {prop?.value}
                                        </Typography>
                                    </ListItem>
                                );
                            }

                            return null;
                        })}
                    </List>
                </Box>

                <Box mb={2}>
                    <Divider />
                </Box>

                <Grid container spacing={2}>
                    {order.values.rentPeriod || order.values.rentDate ? (
                        <Grid item xs={12} md={7}>
                            <LocalizationProvider dateAdapter={MomentUtils} locale="ru">
                                {order.values.rentPeriod && renderRangePicker()}
                                {order.values.rentDate && renderDatePicker()}
                            </LocalizationProvider>
                        </Grid>
                    ) : null}

                    <Grid item xs={12} md={5}>
                        <Box width="100%" display="flex" alignItems="center" justifyContent="space-between" mt={0.8}>
                            <Box minWidth={180} width={isSM ? '50%' : '100%'} display="flex" alignItems="center">
                                <Box mr={1}>
                                    <IconButton size={isSM ? 'small' : 'medium'} onClick={() => store.dec(order.id)}>
                                        <RemoveIcon />
                                    </IconButton>
                                </Box>
                                <Box flex={1} mr={2}>
                                    <TextField
                                        value={order.count}
                                        onChange={e => store.handleCountChange(order.id, e.target.value)}
                                        className={classes.count}
                                        InputProps={{
                                            inputComponent: NumberInput,
                                        }}
                                        fullWidth
                                    />
                                </Box>
                                <IconButton size={isSM ? 'small' : 'medium'} onClick={() => store.inc(order.id)}>
                                    <AddIcon />
                                </IconButton>
                            </Box>

                            {isSM && (
                                <Box ml={3}>
                                    <IconButton size={isSM ? 'small' : 'medium'} onClick={() => store.remove(order.id)}>
                                        <DeleteOutlinedIcon color="primary" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {order.values?.rentHours && (
                    <Grid item xs={12} md={7}>
                        <Box width={isSM ? '100%' : '240px'}>
                            {order.values?.rentHours && (
                                <IncDecNumberInput
                                    name="rentHours"
                                    value={order.values?.rentHours}
                                    onChange={(name, value) => {
                                        store.handleChange(order.id, name, value);
                                    }}
                                    required
                                    smallLabel
                                />
                            )}
                        </Box>
                    </Grid>
                )}
            </Grid>

            <Grid item xs={1}>
                {!isSM && (
                    <IconButton onClick={() => store.remove(order.id)}>
                        <DeleteOutlinedIcon color="primary" />
                    </IconButton>
                )}
            </Grid>
        </Grid>
    );
});

const useStyles = makeStyles(theme => ({
    imgWrap: {
        display: 'block',
        maxWidth: 170,
        margin: '0 auto',
    },
    img: {
        width: '100%',
    },
    propItem: {
        padding: theme.spacing(0.3, 0),
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'space-between',
        },
    },
    price: {
        color: theme.palette.primary.main,
    },
    count: {
        '& input': {
            textAlign: 'center',
        },
    },
    link: {
        color: '#232221',
        transition: 'all .3s ease',
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
}));

export default CartItem;
